/**
 * How the portal recognises a project `<p>` — its catalog Component and the
 * ArgoCD Applications of its environments — the identity contract of the
 * application platform (platform-catalog `contracts/identity.md`, step 6 of
 * the plan, 2026-10-20). The frontend half of
 * `plugins/waadoo-core-node/src/shared/projectIdentity.ts`: the same markers
 * and the same rule, so that a card, the sidebar and the backend routes agree
 * on what is a project.
 *
 * A COMPONENT IS THE PROJECT `<p>` when all three hold:
 *
 *  1. its label `PROJECT_LABEL` is `<p>` — THE PROJECT ID. The label is the
 *     identity; the annotations below only prove the entity is a project;
 *  2. it has an `ownedBy` relation to `group:default/prj-<p>`;
 *  3. it is typed: `APP_TYPE_ANNOTATION` is present and not empty.
 *
 * Seuls les marqueurs génériques sont lus depuis l'étape 13 (nettoyage,
 * 2026-10-22), comme côté backend : les anciens (`odoo/project-id` du
 * Component, label `odoo-project` et annotation `odoo/url` de l'Application)
 * ne font plus d'un objet un projet ni d'une annotation une adresse. Tous
 * les projets portent les nouveaux depuis la migration de l'étape 11.
 *
 * WHY THE LABEL AND THE OWNER, and not an annotation alone: anyone who can
 * push to the branch the catalog reads can publish an entity carrying any
 * annotation. Without the label such an entity is readable by every signed-in
 * user (see `PROJECT_LABEL` in
 * plugins/waadoo-core-backend/src/extensions/permissionsPolicyExtension.ts) and would
 * show up under, or take the breadcrumb of, another project. Labelled and
 * owned by `prj-<p>`, it is readable only by the members of `<p>` (and the
 * admin tier), who already see the real project.
 *
 * AN APPLICATION BELONGS TO `<p>` when its label `APP_PROJECT_LABEL` is `<p>`.
 */

import { RELATION_OWNED_BY, type Entity } from '@backstage/catalog-model';

/** The label naming the project of a Component (and of a Namespace). */
export const PROJECT_LABEL = 'waadoo.ovh/project';

/** The annotation typing a project's Component with its `AppType`. */
export const APP_TYPE_ANNOTATION = 'waadoo.ovh/app-type';

/**
 * Odoo, the only type before the platform: the type the embedded descriptor
 * stands for (`templateRoles.ts`), and the default of the views' DISPLAY
 * while no type is known (`appIcons.ts`, `appTitle.ts`) — un logo et un nom
 * avant lecture, jamais un comportement (une branche dont l'Application
 * n'est pas lue n'a pas de type, `BranchPage.tsx`). Not a marker: no object
 * is read as Odoo for lack of `APP_TYPE_ANNOTATION` or `APP_TYPE_LABEL`
 * (step 13).
 */
export const LEGACY_APP_TYPE = 'odoo';

/**
 * The name the views give a project of `LEGACY_APP_TYPE` while its
 * descriptor's `title` is unread, or cannot be read: Odoo's, in every text
 * before the platform (`appTitle.ts`). Mirrored in
 * `plugins/waadoo-core-node/src/shared/projectIdentity.ts`.
 */
export const LEGACY_APP_TITLE = 'Odoo';

/** The label naming the project of an environment Application. */
export const APP_PROJECT_LABEL = PROJECT_LABEL;

/**
 * Le label donnant l'`AppType` d'une Application d'environnement (posé par
 * les modèles d'appenv, étape 10 ; lu par `applicationAppTypeOf`).
 */
export const APP_TYPE_LABEL = 'waadoo.ovh/app-type';

/** The label naming the branch of an environment Application (unchanged). */
export const APP_BRANCH_LABEL = 'app-branch';

/** The annotation holding an environment's public address. */
export const APP_URL_ANNOTATION = 'waadoo.ovh/url';

/** The group that owns every entity of project `projectId`. */
export function projectOwnerRef(projectId: string): string {
  return `group:default/prj-${projectId}`;
}

/** The fields `projectIdOf` reads, to request alongside any others. */
export const PROJECT_ENTITY_FIELDS = [
  'metadata.annotations',
  'metadata.labels',
  'relations',
];

/**
 * `{ "metadata.annotations.<key>": { $exists: true } }`, as its own object
 * type. Written inline, the two alternatives of one array literal are
 * normalised by TypeScript into a union carrying each other's key as
 * `?: undefined`, which the blueprints' `FilterPredicate`
 * (`@backstage/filter-predicates`, not a dependency of this package) refuses.
 * The assertion only names the key the template literal builds.
 */
function hasAnnotation<K extends string>(
  key: K,
): Record<`metadata.annotations.${K}`, { $exists: boolean }> {
  return { [`metadata.annotations.${key}`]: { $exists: true } } as Record<
    `metadata.annotations.${K}`,
    { $exists: boolean }
  >;
}

/**
 * The entity predicate of the project pages' extensions (cards, contents,
 * layouts): an entity carrying `APP_TYPE_ANNOTATION`. Coarser than
 * `projectIdOf` on purpose — it has to be the exact negation of the filters
 * the app-config files set on the upstream cards it replaces (the annotation
 * `$exists: false`), which a predicate can express and the label/owner check
 * cannot. The components then read the project with `projectIdOf`.
 */
export const PROJECT_ENTITY_FILTER = hasAnnotation(APP_TYPE_ANNOTATION);

/** The exact negation of {@link PROJECT_ENTITY_FILTER}. */
export const NOT_PROJECT_ENTITY_FILTER = { $not: PROJECT_ENTITY_FILTER };

/**
 * The project id of `entity` when it is a genuine project entry point (the
 * three conditions of the header). Undefined otherwise.
 */
export function projectIdOf(entity: Entity): string | undefined {
  const projectId = entity.metadata.labels?.[PROJECT_LABEL];
  if (!projectId) {
    return undefined;
  }
  if (!entity.metadata.annotations?.[APP_TYPE_ANNOTATION]) {
    return undefined;
  }
  const owner = projectOwnerRef(projectId).toLocaleLowerCase('en-US');
  const owned = (entity.relations ?? []).some(
    relation =>
      relation.type === RELATION_OWNED_BY &&
      relation.targetRef.toLocaleLowerCase('en-US') === owner,
  );
  return owned ? projectId : undefined;
}

/**
 * The `AppType` of a project's entity — `APP_TYPE_ANNOTATION` — when
 * `projectIdOf` recognises it. Undefined for any other entity.
 */
export function appTypeOf(entity: Entity): string | undefined {
  return projectIdOf(entity) === undefined
    ? undefined
    : entity.metadata.annotations?.[APP_TYPE_ANNOTATION];
}

/** The slice of an ArgoCD Application these helpers read. */
export interface MarkedApplication {
  metadata: {
    labels?: Record<string, string>;
    annotations?: Record<string, string>;
  };
}

/**
 * The project an environment Application belongs to: `APP_PROJECT_LABEL`.
 * Undefined when it is absent or empty.
 */
export function applicationProjectOf(
  app: MarkedApplication,
): string | undefined {
  return app.metadata.labels?.[APP_PROJECT_LABEL] || undefined;
}

/** An `https://` URL without whitespace (backend `HTTPS_URL`). */
const HTTPS_URL = /^https:\/\/[^\s]+$/;

/**
 * An environment's public address: `APP_URL_ANNOTATION`, kept only when it
 * is an `https://` URL, the same rule as the backend's `appUrlOf`
 * (plugins/waadoo-core-node/src/shared/projectIdentity.ts): the value is
 * rendered as an `href` (environment cards, project service card), and an
 * annotation is writable by whoever edits the appenv, so a `javascript:` or
 * `data:` value must never reach the DOM.
 */
export function applicationUrlOf(app: MarkedApplication): string | undefined {
  const url = app.metadata.annotations?.[APP_URL_ANNOTATION];
  return url !== undefined && HTTPS_URL.test(url) ? url : undefined;
}

/**
 * L'`AppType` d'une Application d'environnement : `APP_TYPE_LABEL`, non vide ;
 * indéfini sans lui (plus de repli sur Odoo depuis l'étape 13 : la page de
 * la branche n'a alors aucun module de type). Lu par la page d'une branche
 * pour choisir le module de type d'application (components/appModules).
 */
export function applicationAppTypeOf(
  app: MarkedApplication,
): string | undefined {
  return app.metadata.labels?.[APP_TYPE_LABEL] || undefined;
}
